import { Download, MapPin, Mic, PlusCircle, X, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import userService from '../../services/userService';
import { Card, SuccessOverlay } from '../ui';
import IconTextButton from '../ui/IconTextButton';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input';
import { productTemplateApi, productionTemplateApi } from './templateApi';

const ProductTemplate = () => {
  const location = useLocation();
  const isEditMode = location.state?.mode === 'edit';
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageName, setImageName] = useState('');
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [productDetails, setProductDetails] = useState({
    productName: '',
    category: '',
    sku: '',
    type: '',
    price: '',
    cost: '',
    unit: '',
    barcode: '',
  });
  const [productType, setProductType] = useState('selling'); // 'selling' or 'material'
  const [description, setDescription] = useState('');
  const [perServe, setPerServe] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0); // Recording time in seconds
  const [recordingTimerInterval, setRecordingTimerInterval] = useState(null);
  const [showUploadRetryDialog, setShowUploadRetryDialog] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [bomEntries, setBomEntries] = useState([
    { raw_material_id: '', quantity: '', unit: '', calories: '', specs: '' }
  ]);
  const [stockItems, setStockItems] = useState([]);
  const [isLoadingStockItems, setIsLoadingStockItems] = useState(false);
  const [calculatingNutrition, setCalculatingNutrition] = useState({}); // Track which entries are calculating
  const [allergenItems, setAllergenItems] = useState([]); // Array of allergen items (read-only, from API)
  const [allergenData, setAllergenData] = useState(null); // Structured allergen data with categorized and list
  const [detectingAllergensLLM, setDetectingAllergensLLM] = useState(false); // Loading state for LLM detection
  const [allergenDetectionMethod, setAllergenDetectionMethod] = useState(null); // 'automatic' or 'llm'
  const [addToMenu, setAddToMenu] = useState(false);
  const [initializedFromTemplate, setInitializedFromTemplate] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  const { openOverlay } = useOverlay();

  // SOP (Standard Operating Procedure) - flexible procedures: [{ id, heading, points }]
  const [sopProcedures, setSopProcedures] = useState([]);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const sitesData = await productTemplateApi.getSites();
        const sitesList = Array.isArray(sitesData)
          ? sitesData
          : sitesData?.sites || sitesData?.data || [];
        
        // Sort sites alphabetically by site name
        const sortedSites = sitesList.sort((a, b) => {
          const nameA = (a.site_name || a.name || '').toLowerCase();
          const nameB = (b.site_name || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
      } catch (error) {
        console.error('Error fetching sites:', error);
        setError('Failed to load sites. Please refresh the page.');
      } finally {
        setIsLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Initialize form from navigation state (edit mode)
  useEffect(() => {
    const state = location.state;
    const template = state?.template;

    if (!template || initializedFromTemplate) {
      return;
    }

    const initializeFromTemplate = async () => {
      // Store the current template ID so that Save updates the existing template
      const templateId =
        template?.product_template_id ||
        template?.production_template_id ||
        template?.template_id ||
        template?.id ||
        template?.productTemplateId ||
        template?.productionTemplateId;
      if (templateId) {
        setCurrentTemplateId(templateId);
      }

      const productName =
        template.template_name ||
        template.product_name ||
        template.productName ||
        '';

      setProductDetails({
        productName,
        category: template.category || '',
        sku: template.sku || '',
        type: template.type || '',
        price:
          template.price != null && !Number.isNaN(Number(template.price))
            ? String(template.price)
            : '',
        cost:
          template.cost != null && !Number.isNaN(Number(template.cost))
            ? String(template.cost)
            : '',
        unit: template.unit || '',
        barcode: template.barcode || '',
      });

      const perServeValue =
        template.per_serve != null ? Number(template.per_serve) : null;
      setPerServe(
        perServeValue != null && !Number.isNaN(perServeValue)
          ? String(perServeValue)
          : ''
      );

      setDescription(template.description || '');

      const selectedSiteFromTemplate =
        template.site_id || template.site_name
          ? {
              site_id: template.site_id,
              site_name: template.site_name,
              id: template.site_id,
              name: template.site_name,
            }
          : null;
      setSelectedSite(selectedSiteFromTemplate);

      setAddToMenu(!!template.add_to_menu);

      const bomFromTemplate = Array.isArray(template.bom)
        ? template.bom.map((entry) => ({
            raw_material_id:
              entry.raw_material_id ||
              entry.production_template_id ||
              entry.item_id ||
              '',
            quantity:
              entry.quantity != null && !Number.isNaN(Number(entry.quantity))
                ? String(entry.quantity)
                : '',
            unit: entry.unit || '',
            calories:
              entry.calories != null && !Number.isNaN(Number(entry.calories))
                ? String(entry.calories)
                : '',
            specs: entry.specs || '',
          }))
        : [];

      if (bomFromTemplate.length > 0) {
        setBomEntries(bomFromTemplate);
      }

      const sopFromTemplate =
        template.sop?.procedures?.map((proc, idx) => ({
          id: proc.sop_no || `sop-${idx + 1}`,
          heading: proc.heading || '',
          points: proc.points || '',
        })) || [];

      if (sopFromTemplate.length > 0) {
        setSopProcedures(sopFromTemplate);
      }

      const allergenItemsFromTemplate =
        (Array.isArray(template.allergens) && template.allergens.length > 0
          ? template.allergens
          : (template.allergen_data?.list || []).map((item) =>
              typeof item === 'string' ? item : item.name
            )
        ).filter(Boolean);

      setAllergenItems(allergenItemsFromTemplate);
      setAllergenData(template.allergen_data || null);
      setAllergenDetectionMethod(
        allergenItemsFromTemplate.length > 0 ? 'automatic' : null
      );

      // Initialize image preview from existing template photo_key (edit mode)
      try {
        const photoKey =
          template.photo_key ||
          template.photoKey ||
          template.photo_url ||
          template.photoUrl;

        if (photoKey) {
          // If backend already returns a direct URL, use it
          if (
            typeof photoKey === 'string' &&
            (photoKey.startsWith('http://') || photoKey.startsWith('https://'))
          ) {
            setImagePreview(photoKey);
            const nameFromUrl =
              photoKey.split('/').pop()?.split('?')[0] || 'Existing image';
            setImageName(nameFromUrl);
          } else if (typeof photoKey === 'string') {
            // Otherwise, treat as S3 key/path and get a presigned URL
            let s3Path;
            if (photoKey.startsWith('s3://')) {
              s3Path = photoKey;
            } else {
              s3Path = `s3://production-template-media/${photoKey}`;
            }

            const presignedUrl = await userService.getS3PresignedUrl(s3Path);
            if (presignedUrl) {
              setImagePreview(presignedUrl);
              const nameFromKey =
                photoKey.split('/').pop() || 'Existing image';
              setImageName(nameFromKey);
            }
          }
        }
      } catch (imageInitError) {
        console.error(
          'Product Template: Error initializing image from template:',
          imageInitError
        );
      }

      setInitializedFromTemplate(true);
    };

    void initializeFromTemplate();
  }, [location.state, initializedFromTemplate]);

  // Initialize Speech Recognition
  useEffect(() => {
    let recognitionInstance = null;

    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'en-US';

        recognitionInstance.onresult = (event) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          if (finalTranscript) {
            setDescription(prev => prev + (prev ? ' ' : '') + finalTranscript.trim());
          }
        };

        recognitionInstance.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsRecording(false);
          if (event.error === 'no-speech') {
            setError('No speech detected. Please try again.');
          } else if (event.error === 'not-allowed') {
            setError('Microphone permission denied. Please enable microphone access.');
          } else {
            setError('Speech recognition error. Please try again.');
          }
        };

        recognitionInstance.onend = () => {
          setIsRecording(false);
        };

        setRecognition(recognitionInstance);
      }
    }

    return () => {
      if (recognitionInstance) {
        try {
          recognitionInstance.stop();
        } catch (error) {
          // Ignore errors when stopping recognition on cleanup
        }
      }
    };
  }, []);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Timer effect for recording
  useEffect(() => {
    if (isRecording) {
      // Start timer
      const interval = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Stop recording at 10 minutes (600 seconds)
          if (newTime >= 600) {
            // Stop recording automatically
            if (recognition) {
              recognition.stop();
            }
            if (mediaRecorder && mediaRecorder.state !== 'inactive') {
              mediaRecorder.stop();
            }
            setIsRecording(false);
            setError('Recording stopped automatically after 10 minutes.');
            return 600;
          }
          return newTime;
        });
      }, 1000); // Update every second

      setRecordingTimerInterval(interval);

      return () => {
        clearInterval(interval);
      };
    } else {
      // Reset timer when not recording
      setRecordingTime(0);
      if (recordingTimerInterval) {
        clearInterval(recordingTimerInterval);
        setRecordingTimerInterval(null);
      }
    }
  }, [isRecording, recognition, mediaRecorder]);

  // Format time as MM:SS
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // SOP: split multiline text into non-empty lines for bullet display
  const sopLines = (text) =>
    (text || '')
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(Boolean);

  const addSopProcedure = () => {
    setSopProcedures(prev => [
      ...prev,
      { id: crypto.randomUUID?.() ?? `sop-${Date.now()}-${prev.length}`, heading: '', points: '' },
    ]);
  };

  const updateSopProcedure = (id, field, value) => {
    setSopProcedures(prev =>
      prev.map(p => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const removeSopProcedure = (id) => {
    setSopProcedures(prev => prev.filter(p => p.id !== id));
  };

  // Fetch stock items when site is selected (for BOM raw material selection)
  useEffect(() => {
    const fetchStockItems = async () => {
      if (!selectedSite?.site_id && !selectedSite?.id) {
        setStockItems([]);
        return;
      }

      setIsLoadingStockItems(true);
      try {
        const siteId = selectedSite.site_id || selectedSite.id;
        const result = await productionTemplateApi.getStockItems({
          siteId: siteId,
          limit: 500,
        });

        if (result.success && result.items) {
          const items = result.items.map(item => ({
            id: item.stock_item_id || item.id || item._id,
            name: item.stock_name || item.name || item.item_name || 'Unknown Item',
            stock_item_id: item.stock_item_id || item.id || item._id,
            unit: item.unit || item.unit_of_measure || '',
          })).sort((a, b) => a.name.localeCompare(b.name));
          
          setStockItems(items);
        } else {
          setStockItems([]);
        }
      } catch (error) {
        console.error('Error fetching stock items:', error);
        setStockItems([]);
      } finally {
        setIsLoadingStockItems(false);
      }
    };

    fetchStockItems();
  }, [selectedSite]);

  const handleSiteChange = siteId => {
    const site = sites.find(s => (s.site_id || s.id) === siteId);
    setSelectedSite(site || null);
  };

  const isFormValid = useMemo(() => {
    // Product name is required
    const hasProductName = productDetails.productName.trim().length > 0;
    
    // BOM is required and must be non-empty with valid entries
    const hasValidBom = bomEntries.length > 0 && bomEntries.every(entry => 
      entry.raw_material_id && 
      entry.raw_material_id.trim() !== '' &&
      entry.quantity && 
      parseFloat(entry.quantity) > 0 &&
      entry.unit && 
      entry.unit.trim() !== ''
    );
    
    return hasProductName && hasValidBom;
  }, [productDetails, bomEntries]);

  // Calculate total calories from all BOM entries
  const totalCalories = useMemo(() => {
    return bomEntries.reduce((total, entry) => {
      if (entry.calories && entry.calories.trim() !== '') {
        const calories = parseFloat(entry.calories);
        if (!isNaN(calories) && calories > 0) {
          return total + calories;
        }
      }
      return total;
    }, 0);
  }, [bomEntries]);

  // Calculate calories per serve
  const caloriesPerServe = useMemo(() => {
    if (!perServe || perServe.trim() === '') {
      return null;
    }
    const serveValue = parseFloat(perServe);
    if (isNaN(serveValue) || serveValue <= 0) {
      return null;
    }
    if (totalCalories === 0) {
      return 0;
    }
    return totalCalories / serveValue;
  }, [totalCalories, perServe]);

  const handleImageUpload = event => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setImageFile(file);
    setImageName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result?.toString() || '');
    };
    reader.readAsDataURL(file);
  };

  const handleDiscardImage = () => {
    setImagePreview('');
    setImageFile(null);
    setImageName('');
  };

  const handleDetailChange = (field, value) => {
    setProductDetails(prev => ({ ...prev, [field]: value }));
  };

  // BOM management functions
  const handleAddBomEntry = () => {
    setBomEntries(prev => [...prev, { raw_material_id: '', quantity: '', unit: '', calories: '', specs: '' }]);
  };

  const handleRemoveBomEntry = (index) => {
    setBomEntries(prev => prev.filter((_, i) => i !== index));
    // Clear calculating state for removed entry
    setCalculatingNutrition(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
            // Clear allergens when BOM entries change (will be recalculated)
            setAllergenItems([]);
            setAllergenData(null);
            setAllergenDetectionMethod(null);
  };

  // Calculate nutrition for all BOM entries
  const calculateAllNutrition = async () => {
    const validEntries = bomEntries.filter(
      entry =>
        entry.raw_material_id &&
        entry.quantity &&
        entry.unit &&
        parseFloat(entry.quantity) > 0
    );

    if (validEntries.length === 0) {
      setError('Please fill in raw material, quantity, and unit for at least one entry.');
      return;
    }

    // Set calculating state for all valid entries
    const calculatingState = {};
    validEntries.forEach((_, index) => {
      const originalIndex = bomEntries.findIndex(
        (e, i) =>
          e.raw_material_id === validEntries[index].raw_material_id &&
          e.quantity === validEntries[index].quantity &&
          e.unit === validEntries[index].unit
      );
      if (originalIndex !== -1) {
        calculatingState[originalIndex] = true;
      }
    });
    setCalculatingNutrition(calculatingState);

    try {
      const bom = validEntries.map(entry => ({
        raw_material_id: entry.raw_material_id,
        quantity: parseFloat(entry.quantity),
        unit: entry.unit.trim(),
      }));

      const result = await productionTemplateApi.calculateNutritionFromBOM({
        bom,
        recalculate: false,
      });

      if (result.success && result.nutritionBreakdown?.ingredients?.length > 0) {
        // Create a map of raw_material_id to calories
        const caloriesMap = {};
        result.nutritionBreakdown.ingredients.forEach(ingredient => {
          caloriesMap[ingredient.raw_material_id] = ingredient.calories || 0;
        });

        // Update all entries with calculated calories
        setBomEntries(prev =>
          prev.map(entry => {
            if (
              entry.raw_material_id &&
              entry.quantity &&
              entry.unit &&
              caloriesMap[entry.raw_material_id] !== undefined
            ) {
              return {
                ...entry,
                calories: caloriesMap[entry.raw_material_id].toFixed(2),
              };
            }
            return entry;
          })
        );

        // Extract allergens from the API response
        // Handle both array format (legacy) and structured format (new)
        if (result.allergens) {
          if (Array.isArray(result.allergens) && result.allergens.length > 0) {
            // Legacy format: array of strings
            setAllergenItems(result.allergens);
            setAllergenData(null);
          } else if (result.allergens.list && Array.isArray(result.allergens.list)) {
            // New structured format: { categorized: {}, list: [] }
            setAllergenData(result.allergens);
            // Extract allergen names for backward compatibility
            const allergenNames = result.allergens.list.map(item => 
              typeof item === 'string' ? item : item.name
            );
            setAllergenItems(allergenNames);
          } else {
            setAllergenItems([]);
            setAllergenData(null);
          }
          setAllergenDetectionMethod('automatic');
        } else {
          setAllergenItems([]);
          setAllergenData(null);
        }

        const calculatedCount = result.nutritionBreakdown.ingredients.length;
        const unavailableCount =
          result.nutritionBreakdown.unavailable_ingredients?.length || 0;

        if (calculatedCount > 0) {
          setSuccess(
            `✅ Calculated nutrition for ${calculatedCount} ingredient(s)${
              unavailableCount > 0
                ? `. ${unavailableCount} ingredient(s) not found in database.`
                : ''
            }`
          );
        }
      } else {
        setError(
          'Could not calculate nutrition. Please check your entries and try again.'
        );
      }
    } catch (error) {
      console.error('Error calculating nutrition for all entries:', error);
      setError(
        'Failed to calculate nutrition. You can enter calories manually.'
      );
    } finally {
      // Clear calculating state
      setCalculatingNutrition({});
    }
  };

  // Detect allergens using LLM (AI-powered)
  const detectAllergensWithLLM = async () => {
    // Extract ingredient names from BOM entries
    const validEntries = bomEntries.filter(
      entry =>
        entry.raw_material_id &&
        entry.raw_material_id.trim() !== ''
    );

    if (validEntries.length === 0) {
      setError('Please add at least one raw material to detect allergens.');
      return;
    }

    // Get ingredient names from stock items
    const ingredients = validEntries
      .map(entry => {
        const stockItem = stockItems.find(
          item => (item.stock_item_id || item.id) === entry.raw_material_id
        );
        return stockItem?.name || entry.raw_material_id;
      })
      .filter(Boolean);

    if (ingredients.length === 0) {
      setError('Could not extract ingredient names. Please ensure raw materials are selected.');
      return;
    }

    setDetectingAllergensLLM(true);
    setError('');

    try {
      const result = await productionTemplateApi.detectAllergensLLM({
        ingredients,
      });

      if (result.success && result.allergens) {
        setAllergenData(result.allergens);
        // Extract allergen names for backward compatibility
        const allergenNames = result.allergens.list
          ? result.allergens.list.map(item =>
              typeof item === 'string' ? item : item.name
            )
          : [];
        setAllergenItems(allergenNames);
        setAllergenDetectionMethod('llm');
        setSuccess(
          `✅ Detected ${allergenNames.length} allergen(s) using AI-powered detection.`
        );
      } else {
        setError('Could not detect allergens. Please try again.');
        setAllergenData(null);
        setAllergenItems([]);
      }
    } catch (error) {
      console.error('Error detecting allergens with LLM:', error);
      setError(
        error.message ||
          'Failed to detect allergens with AI. You can use automatic detection instead.'
      );
    } finally {
      setDetectingAllergensLLM(false);
    }
  };

  // Calculate nutrition for a single BOM entry
  const calculateNutritionForEntry = async (index, entry) => {
    // Check if all required fields are filled
    if (!entry.raw_material_id || !entry.quantity || !entry.unit) {
      return;
    }

    // Validate quantity
    const quantity = parseFloat(entry.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      return;
    }

    // Set calculating state
    setCalculatingNutrition(prev => ({ ...prev, [index]: true }));

    try {
      const result = await productionTemplateApi.calculateNutritionFromBOM({
        bom: [
          {
            raw_material_id: entry.raw_material_id,
            quantity: quantity,
            unit: entry.unit.trim(),
          },
        ],
        recalculate: false,
      });

      if (result.success && result.nutritionBreakdown?.ingredients?.length > 0) {
        const ingredient = result.nutritionBreakdown.ingredients[0];
        const calories = ingredient.calories || 0;

        // Update the calories field for this entry
        setBomEntries(prev =>
          prev.map((e, i) => (i === index ? { ...e, calories: calories.toFixed(2) } : e))
        );

        // Show success message if calories were found
        if (calories > 0) {
          console.log(
            `✅ Nutrition calculated for entry ${index + 1}: ${calories.toFixed(2)} kcal`
          );
        } else {
          console.warn(
            `⚠️ Nutrition calculated but calories = 0 for entry ${index + 1}`
          );
        }
      } else {
        // No calories found or calculation failed
        console.warn(
          `⚠️ Could not calculate nutrition for entry ${index + 1}. You can enter calories manually.`
        );
      }
    } catch (error) {
      console.error(`Error calculating nutrition for entry ${index + 1}:`, error);
      // Don't show error to user - they can still enter calories manually
    } finally {
      // Clear calculating state
      setCalculatingNutrition(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  const handleBomChange = (index, field, value) => {
    setBomEntries(prev => {
      const updatedEntries = prev.map((entry, i) => {
        if (i === index) {
          const updatedEntry = { ...entry, [field]: value };
          
          // Auto-fill unit when raw material is selected
          if (field === 'raw_material_id' && value) {
            const selectedItem = stockItems.find(item => 
              (item.stock_item_id || item.id) === value
            );
            if (selectedItem && selectedItem.unit) {
              updatedEntry.unit = selectedItem.unit;
            }
            // Clear calories when raw material changes
            updatedEntry.calories = '';
            // Clear allergens when raw materials change (will be recalculated)
            setAllergenItems([]);
            setAllergenData(null);
            setAllergenDetectionMethod(null);
          }
          
          return updatedEntry;
        }
        return entry;
      });

      // After updating, check if we should calculate nutrition
      const updatedEntry = updatedEntries[index];
      if (
        updatedEntry.raw_material_id &&
        updatedEntry.quantity &&
        updatedEntry.unit &&
        field !== 'calories' && // Don't trigger calculation when manually editing calories
        field !== 'specs' // Don't trigger calculation when editing specifications
      ) {
        // Debounce: Calculate nutrition after a short delay
        setTimeout(() => {
          calculateNutritionForEntry(index, updatedEntry);
        }, 500);
      }

      return updatedEntries;
    });
  };

  // Separate function to handle image upload
  const uploadImageToTemplate = async templateId => {
    if (!imageFile) {
      return { success: true, skipped: true };
    }

    try {
      console.log('Product Template: Starting image upload process...');

      // Get presigned URL
      const presignResult = await productTemplateApi.presignPhotoUpload(
        templateId,
        imageFile.type || 'image/jpeg'
      );

      if (!presignResult.success) {
        console.warn(
          '⚠️ Product Template: Failed to get presigned URL:',
          presignResult.message
        );
        return {
          success: false,
          error: presignResult.error || 'Upload failed',
          message: presignResult.message || 'Failed to get presigned URL',
        };
      }

      console.log(
        'Product Template: Presigned URL obtained, uploading to S3...'
      );

      // Upload to S3
      const uploadResult = await productTemplateApi.uploadPhotoToS3(
        presignResult,
        imageFile
      );

      if (!uploadResult.success) {
        console.warn(
          '⚠️ Product Template: Failed to upload image:',
          uploadResult.message
        );
        return {
          success: false,
          error: uploadResult.error || 'Upload failed',
          message: uploadResult.message || 'Failed to upload image to S3',
        };
      }

      console.log(
        'Product Template: Image uploaded to S3, extracting photo_key...'
      );

      // Extract photo_key from upload result
      const photoKey =
        uploadResult.photoKey ||
        presignResult.photoKey ||
        presignResult.data?.photo_key ||
        presignResult.postData?.fields?.key;

      if (photoKey) {
        try {
          console.log(
            'Product Template: Updating template with photo_key:',
            photoKey,
            '(using production template API to avoid CORS)'
          );
          // Use production template API (same backend, CORS configured)
          await productionTemplateApi.updateTemplate(templateId, {
            photo_key: photoKey,
          });
          console.log(
            '✅ Product Template: Template photo_key updated successfully'
          );
          return { success: true, photoKey };
        } catch (updateError) {
          console.warn(
            '⚠️ Product Template: Failed to update template with photo_key:',
            updateError
          );
          return {
            success: false,
            error: 'Update failed',
            message: `Image uploaded but failed to update photo_key: ${updateError.message}`,
          };
        }
      } else {
        console.warn(
          '⚠️ Product Template: Could not extract photo_key from S3 URI'
        );
        return {
          success: false,
          error: 'Extraction failed',
          message:
            'Image uploaded but could not extract photo_key. Please update manually.',
        };
      }
    } catch (imageError) {
      console.error('❌ Product Template: Error uploading image:', imageError);
      return {
        success: false,
        error: 'Upload error',
        message: imageError.message || 'Failed to upload image',
      };
    }
  };

  const handleRetryImageUpload = async () => {
    if (!pendingTemplateId) {
      setShowUploadRetryDialog(false);
      return;
    }

    setIsSubmitting(true);
    setShowUploadRetryDialog(false);
    setUploadError(null);

    const result = await uploadImageToTemplate(pendingTemplateId);

    if (result.success) {
      setSuccess('Product template created successfully with image!');
      handleFormReset();
    } else {
      // Show retry dialog again
      setUploadError(result);
      setShowUploadRetryDialog(true);
    }

    setIsSubmitting(false);
  };

  const handleSkipImageUpload = () => {
    setShowUploadRetryDialog(false);
    setUploadError(null);
    setPendingTemplateId(null);
    setSuccess('Product template created successfully! (Image upload skipped)');
    handleFormReset();
    setIsSubmitting(false);
  };

  const handleToggleRecording = async () => {
    if (isRecording) {
      // Stop both speech recognition and audio recording
      if (recognition) {
        recognition.stop();
      }
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      setIsRecording(false);
      setRecordingTime(0);
    } else {
      try {
        // Reset timer
        setRecordingTime(0);
        
        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Start audio recording
        const recorder = new MediaRecorder(stream);
        const chunks = [];

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          setAudioBlob(blob);
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
          
          // Stop all tracks to release microphone
          stream.getTracks().forEach(track => track.stop());
        };

        recorder.onerror = (event) => {
          console.error('MediaRecorder error:', event.error);
          setError('Error recording audio. Please try again.');
          stream.getTracks().forEach(track => track.stop());
          setIsRecording(false);
          setRecordingTime(0);
        };

        recorder.start();
        setMediaRecorder(recorder);

        // Start speech recognition if available
        if (recognition) {
          try {
            recognition.start();
          } catch (recError) {
            console.warn('Speech recognition already started or error:', recError);
          }
        }

        setIsRecording(true);
        setError('');
      } catch (error) {
        console.error('Error starting recording:', error);
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setError('Microphone permission denied. Please enable microphone access in your browser settings.');
        } else if (error.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone and try again.');
        } else {
          setError('Failed to start recording. Please try again.');
        }
        setIsRecording(false);
        setRecordingTime(0);
      }
    }
  };

  const handleDeleteAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setAudioBlob(null);
  };

  const handleFormReset = () => {
    if (recognition && isRecording) {
      recognition.stop();
    }
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    setIsRecording(false);
    setRecordingTime(0);
    
    // Clean up audio URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setAudioBlob(null);
    
    setProductDetails({
      productName: '',
      category: '',
      sku: '',
      price: '',
      cost: '',
      unit: '',
      barcode: '',
    });
    setDescription('');
    setPerServe('');
    setImagePreview('');
    setImageFile(null);
    setImageName('');
    setSelectedSite(null);
    setAddToMenu(false);
    setBomEntries([{ raw_material_id: '', quantity: '', unit: '', calories: '', specs: '' }]);
    setAllergenItems([]);
    setAllergenData(null);
    setAllergenDetectionMethod(null);
    setSopProcedures([]);
    setCurrentTemplateId(null);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!isFormValid) {
      if (!productDetails.productName.trim()) {
        setError('Please fill in the product name.');
      } else if (bomEntries.length === 0 || !bomEntries.every(entry => 
        entry.raw_material_id && entry.quantity && entry.unit
      )) {
        setError('Please add at least one BOM entry with raw material, quantity, and unit.');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare template payload
      // API expects 'template_name' field (not 'product_name')
      // Prepare BOM array - filter out empty entries and validate
      const validBomEntries = bomEntries
        .filter(entry => entry.raw_material_id && entry.raw_material_id.trim() !== '')
        .map(entry => {
          const id = entry.raw_material_id.trim();
          const stockItem = stockItems.find(
            item => (item.stock_item_id || item.id) === id
          );

          return {
            raw_material_id: id,
            raw_material_name: stockItem?.name || undefined,
            quantity: parseFloat(entry.quantity),
            unit: entry.unit.trim(),
            ...(entry.calories && entry.calories.trim() !== ''
              ? { calories: parseFloat(entry.calories) }
              : {}),
            ...(entry.specs && entry.specs.trim() !== ''
              ? { specs: entry.specs.trim() }
              : {}),
          };
        });

      if (validBomEntries.length === 0) {
        setError('BOM is required. Please add at least one raw material entry.');
        setIsSubmitting(false);
        return;
      }

      const templatePayload = {
        template_name: productDetails.productName, // API requires 'template_name'
        category: productDetails.category || undefined,
        sku: productDetails.sku || undefined,
        price: productDetails.price ? parseFloat(productDetails.price) : undefined,
        cost: productDetails.cost ? parseFloat(productDetails.cost) : undefined,
        unit: productDetails.unit || undefined,
        barcode: productDetails.barcode || undefined,
        type: productType || undefined,
        description: description || undefined,
        per_serve: perServe ? parseFloat(perServe) : undefined,
        status: 'active', // Default status
        site_id: selectedSite?.site_id || selectedSite?.id || undefined,
        site_name: selectedSite?.site_name || selectedSite?.name || undefined,
        add_to_menu: addToMenu,
        bom: validBomEntries, // Required non-empty BOM array
        // calories_per_serve: computed as totalCalories / per_serve when both available
        ...(caloriesPerServe != null && !Number.isNaN(caloriesPerServe)
          ? { calories_per_serve: Math.round(caloriesPerServe * 100) / 100 }
          : {}),
        // Include allergens if detected
        ...(allergenItems && allergenItems.length > 0 ? { 
          allergens: allergenItems 
        } : {}),
        // Include structured allergen data if available
        ...(allergenData ? { 
          allergen_data: allergenData 
        } : {}),
        // SOP (Standard Operating Procedure) - API shape: procedures[{ sop_no?, heading, points }], estimated_calories
        ...(sopProcedures.some(p => (p.heading || '').trim() || (p.points || '').trim())
          ? {
              sop: {
                procedures: sopProcedures
                  .filter(p => (p.heading || '').trim() || (p.points || '').trim())
                  .map((p, i) => ({
                    sop_no: String(i + 1),
                    heading: (p.heading || '').trim(),
                    points: (p.points || '').trim(),
                  })),
                estimated_calories: totalCalories ?? undefined,
              },
            }
          : {}),
      };

      // If in edit mode and we have an existing template ID, update instead of creating a new one
      if (isEditMode && currentTemplateId) {
        const updateResult = await productTemplateApi.updateTemplate(
          currentTemplateId,
          templatePayload
        );

        if (!updateResult.success) {
          throw new Error(updateResult.message || 'Failed to update template');
        }

        // Upload/replace image if a new one is selected
        if (imageFile) {
          const uploadResult = await uploadImageToTemplate(currentTemplateId);

          if (!uploadResult.success) {
            const isCorsError =
              uploadResult.error === 'CORS Error' ||
              uploadResult.message?.includes('CORS') ||
              uploadResult.message?.includes('preflight');

            if (isCorsError) {
              setSuccess(
                'Product template updated successfully! (Image upload skipped due to CORS configuration issue. Please contact backend team to enable CORS for the presign endpoint.)'
              );
              setShowSuccessScreen(true);
              handleFormReset();
              setIsSubmitting(false);
              return;
            } else {
              setPendingTemplateId(currentTemplateId);
              setUploadError(uploadResult);
              setShowUploadRetryDialog(true);
              setIsSubmitting(false);
              return;
            }
          }
        }

        setSuccess('Product template updated successfully!');
        setShowSuccessScreen(true);
        handleFormReset();
      } else {
        // Create a new template
        const createResult = await productTemplateApi.createTemplate(
          templatePayload
        );

        if (!createResult.success) {
          throw new Error(createResult.message || 'Failed to create template');
        }

        // Try multiple paths to find the template ID
        // Note: API may return production_template_id instead of product_template_id
        const templateId =
          createResult.template?.product_template_id ||
          createResult.template?.production_template_id || // API returns this
          createResult.template?.id ||
          createResult.data?.template?.product_template_id ||
          createResult.data?.template?.production_template_id ||
          createResult.data?.template?.id ||
          createResult.data?.product_template_id ||
          createResult.data?.production_template_id ||
          createResult.data?.id ||
          createResult.product_template_id ||
          createResult.production_template_id ||
          createResult.id ||
          createResult.response?.template?.product_template_id ||
          createResult.response?.template?.production_template_id ||
          createResult.response?.template?.id ||
          createResult.response?.product_template_id ||
          createResult.response?.production_template_id ||
          createResult.response?.id;

        if (!templateId) {
          console.error('Template creation response structure:', createResult);
          console.error('Full response data:', createResult.data);
          console.error('Full response template:', createResult.template);
          console.error('Full response object:', createResult.response);
          throw new Error(
            'Template created but ID not returned. Please check the console for response details.'
          );
        }

        console.log('Product Template: Extracted template ID:', templateId);

        // Step 2: Upload image if provided
        if (imageFile) {
          const uploadResult = await uploadImageToTemplate(templateId);

          if (!uploadResult.success) {
            // Check if it's a CORS error - if so, treat as non-blocking
            const isCorsError =
              uploadResult.error === 'CORS Error' ||
              uploadResult.message?.includes('CORS') ||
              uploadResult.message?.includes('preflight');

            if (isCorsError) {
              // CORS errors are backend configuration issues - show success with warning
              setSuccess(
                'Product template created successfully! (Image upload skipped due to CORS configuration issue. Please contact backend team to enable CORS for the presign endpoint.)'
              );
              setShowSuccessScreen(true);
              handleFormReset();
              setIsSubmitting(false);
              return;
            } else {
              // For other errors, show retry/skip dialog
              setPendingTemplateId(templateId);
              setUploadError(uploadResult);
              setShowUploadRetryDialog(true);
              setIsSubmitting(false);
              return; // Don't reset form yet, wait for user decision
            }
          }
        }

        // Success - template created (with or without image)
        setSuccess('Product template created successfully!');
        setShowSuccessScreen(true);
        handleFormReset();
      }
    } catch (error) {
      console.error('Error creating template:', error);
      setError(error.message || 'Failed to create template. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <form
        onSubmit={handleSubmit}
        className="mx-auto flex w-full flex-col gap-2 lg:flex-row"
      >
        <div className="flex-[1.6]">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-800">
                Product Information
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Create a product template for finished goods/products
              </p>
            </div>
            <IconTextButton
              icon={<Download className="w-5 h-5" />}
              text="Preview & Download PDF"
              onClick={() =>
                openOverlay('productTemplatePdfPreview', {
                  imagePreview,
                  productDetails,
                  selectedSite,
                  addToMenu,
                  productType,
                  bomEntries,
                  stockItems,
                  totalCalories,
                  perServe,
                  caloriesPerServe,
                  sopProcedures,
                  description,
                  allergenItems,
                  allergenData,
                })
              }
              variant="default"
              size="sm"
            />
          </div>

          {/* Two Column Layout: Image on Left, Form on Right */}
          <div className="grid gap-6 md:grid-cols-[354px_1fr]">
            {/* Left Column: Product Image */}
            <div className="flex flex-col">
              <div className="mb-4 text-lg font-semibold text-gray-800">
                Product Image
              </div>
              <div className="rounded-3xl border border-dashed border-blue-200 bg-blue-50/60 p-4 max-w-[354px]">
                <div className="mb-4 relative flex h-80 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-inner">
                  {imagePreview ? (
                    <>
                      <img
                        src={imagePreview}
                        alt="Product preview"
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleDiscardImage}
                        className="absolute top-2 right-2 flex items-center justify-center w-8 h-8 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors shadow-lg"
                        title="Discard image"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </>
                  ) : (
                    <p className="text-center text-gray-400">
                      Upload a product image to preview here
                    </p>
                  )}
                </div>
                <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border border-blue-300 bg-white px-4 py-3 text-sm font-semibold text-blue-600 transition hover:bg-blue-50">
                  <span>Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
              </div>
            </div>

            {/* Right Column: Product Information Grid */}
            <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                Product Name <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Enter product name"
                value={productDetails.productName}
                onChange={event =>
                  handleDetailChange('productName', event.target.value)
                }
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                Category
              </label>
              <Input
                placeholder="e.g., Food Items, Beverages"
                value={productDetails.category}
                onChange={event =>
                  handleDetailChange('category', event.target.value)
                }
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                SKU (Stock Keeping Unit)
              </label>
              <Input
                placeholder="e.g., PS-001"
                value={productDetails.sku}
                onChange={event =>
                  handleDetailChange('sku', event.target.value)
                }
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                Product Type
              </label>
              {/* Toggle Button - Commented Out */}
              {/* <Card.Light className="p-0 border-2 border-gray flex overflow-hidden w-full">
                <button
                  type="button"
                  onClick={() => setProductType('selling')}
                  className={`flex-1 px-6 py-2.5 text-sm font-semibold transition-all ${
                    productType === 'selling'
                      ? 'text-black'
                      : 'text-gray-600'
                  }`}
                  style={
                    productType === 'selling'
                      ? {
                          background:
                            'radial-gradient(184.58% 152.09% at 136.24% -51.3%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%)',
                          boxShadow:
                            '0px 15px 30px 0px rgba(0,0,0,0.10), 0px 5px 20.100000381469727px 0px rgba(255,255,255,0.35) inset',
                          outline: '1px solid white',
                          backdropFilter: 'blur(30px)',
                        }
                      : {}
                  }
                >
                  Selling
                </button>
                <button
                  type="button"
                  onClick={() => setProductType('material')}
                  className={`flex-1 px-6 py-2.5 text-sm font-semibold transition-all ${
                    productType === 'material'
                      ? 'text-gray-800'
                      : 'text-gray-600'
                  }`}
                  style={
                    productType === 'material'
                      ? {
                          background:
                            'radial-gradient(184.58% 152.09% at 136.24% -51.3%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%)',
                          boxShadow:
                            '0px 15px 30px 0px rgba(0,0,0,0.10), 0px 5px 20.100000381469727px 0px rgba(255,255,255,0.35) inset',
                          outline: '1px solid white',
                          backdropFilter: 'blur(30px)',
                        }
                      : {}
                  }
                >
                  Product material
                </button>
              </Card.Light> */}
              
              {/* Dropdown for Product Type */}
              <select
                value={productType}
                onChange={e => setProductType(e.target.value)}
                className="w-full h-12 rounded-2xl border border-gray-200 bg-white px-4 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              >
                <option value="selling">Selling</option>
                <option value="material">Product material</option>
              </select>
            </div>
            {productType === 'selling' && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">
                    Selling Price
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={productDetails.price}
                    onChange={event =>
                      handleDetailChange('price', event.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">
                    Cost Price
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={productDetails.cost}
                    onChange={event =>
                      handleDetailChange('cost', event.target.value)
                    }
                  />
                </div>
              </>
            )}
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                Unit
              </label>
              <Input
                placeholder="e.g., pcs, kg, liters"
                value={productDetails.unit}
                onChange={event =>
                  handleDetailChange('unit', event.target.value)
                }
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                Barcode
              </label>
              <Input
                placeholder="EAN/UPC barcode"
                value={productDetails.barcode}
                onChange={event =>
                  handleDetailChange('barcode', event.target.value)
                }
              />
            </div>
            <div className="md:col-span-2">
              <label
                htmlFor="site"
                className="mb-1 block text-xs font-semibold text-gray-500"
              >
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Select Site
                </span>
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  id="site"
                  value={
                    selectedSite ? selectedSite.site_id || selectedSite.id : ''
                  }
                  onChange={e => handleSiteChange(e.target.value)}
                  disabled={isLoadingSites}
                  className="h-12 min-w-0 flex-1 rounded-2xl border border-gray-200 bg-white px-4 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {isLoadingSites ? 'Loading sites...' : 'Select a site (optional)'}
                  </option>
                  {sites.map(site => (
                    <option
                      key={site.site_id || site.id}
                      value={site.site_id || site.id}
                    >
                      {site.site_name || site.name || 'Unknown Site'}
                    </option>
                  ))}
                </select>
                <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={addToMenu}
                    onChange={e => setAddToMenu(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Add this to menu
                  </span>
                </label>
              </div>
              {/* {selectedSite && (
                <p className="mt-1 text-xs text-gray-500">
                  Selected: {selectedSite.site_name || selectedSite.name}
                </p>
              )} */}
            </div>
            </div>
          </div>
          {/* End of Product Information Section */}

          {/* BOM (Bill of Materials) Section */}
          <div className="mt-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-800">
                  Bill of Materials (BOM) <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500">
                  Add raw materials/ingredients required to produce this product
                </p>
              </div>
              <div className="flex items-center gap-2">
                {bomEntries.some(
                  entry =>
                    entry.raw_material_id && entry.quantity && entry.unit
                ) && (
                  <button
                    type="button"
                    onClick={calculateAllNutrition}
                    disabled={
                      Object.keys(calculatingNutrition).length > 0 ||
                      isSubmitting
                    }
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    title="Calculate nutrition for all entries"
                  >
                    {Object.keys(calculatingNutrition).length > 0 ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Calculating...
                      </>
                    ) : (
                      'Calculate All Nutrition'
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAddBomEntry}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold transition hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  style={{ color: 'white' }}
                >
                  <PlusCircle className="w-4 h-4" />
                  Add Item
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {bomEntries.map((entry, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-gray-200 bg-white p-4"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Item {index + 1}
                    </span>
                    {bomEntries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveBomEntry(index)}
                        className="rounded-lg p-1 text-red-600 transition hover:bg-red-50"
                        title="Remove item"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                    {/* Raw Material Selection */}
                    <div className="lg:col-span-2">
                      <label className="mb-1 block text-xs font-semibold text-gray-500">
                        Raw Material <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={entry.raw_material_id}
                        onChange={e => handleBomChange(index, 'raw_material_id', e.target.value)}
                        required
                        disabled={!selectedSite}
                        className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {!selectedSite
                            ? 'Select site first'
                            : isLoadingStockItems
                            ? 'Loading items...'
                            : stockItems.length === 0
                            ? 'No items available'
                            : 'Select raw material'}
                        </option>
                        {stockItems.map(item => (
                          <option key={item.id} value={item.stock_item_id || item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-gray-500">
                        Quantity <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        min="0.0001"
                        step="0.0001"
                        placeholder="0.0000"
                        value={entry.quantity}
                        onChange={e => handleBomChange(index, 'quantity', e.target.value)}
                        required
                      />
                    </div>

                    {/* Unit */}
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-gray-500">
                        Unit <span className="text-red-500">*</span>
                      </label>
                      <Input
                        placeholder="e.g., pcs, grams, kg"
                        value={entry.unit}
                        disabled={true}
                        readOnly={true}
                        required
                        className="bg-gray-100 cursor-not-allowed"
                        title="Unit is automatically set from the selected raw material"
                      />
                    </div>

                    {/* Calories */}
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-gray-500">
                        Calories
                        {calculatingNutrition[index] && (
                          <span className="ml-2 text-xs text-blue-600 font-normal">
                            (Calculating...)
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={calculatingNutrition[index] ? 'Calculating...' : '0.00'}
                          value={entry.calories}
                          readOnly={true}
                          disabled={true}
                          className="bg-gray-100 cursor-not-allowed"
                          title="Calories are automatically calculated from raw materials"
                        />
                        {calculatingNutrition[index] && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                      </div>
                      {entry.raw_material_id && entry.quantity && entry.unit && !calculatingNutrition[index] && (
                        <button
                          type="button"
                          onClick={() => calculateNutritionForEntry(index, entry)}
                          className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline"
                          title="Recalculate nutrition"
                        >
                          Recalculate
                        </button>
                      )}
                    </div>

                    {/* Specs (Optional) */}
                    <div className="md:col-span-2 lg:col-span-5">
                      <label className="mb-1 block text-xs font-semibold text-gray-500">
                        Specifications (Optional)
                      </label>
                      <Input
                        placeholder="e.g., fresh, organic, specific grade"
                        value={entry.specs}
                        onChange={e => handleBomChange(index, 'specs', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {bomEntries.length === 0 && (
              <div className="mt-4 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3">
                <p className="text-sm text-yellow-800">
                  At least one BOM entry is required. Click "Add Item" to add raw materials.
                </p>
              </div>
            )}

            {/* Total Calories Display */}
            {bomEntries.length > 0 && (
              <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm font-semibold text-gray-700">
                  Total Calories
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {totalCalories.toFixed(2)}
                  <span className="text-sm font-normal text-gray-600 ml-1">kcal</span>
                </p>
              </div>
            )}
          </div>

          {/* SOP (Standard Operating Procedure) Section - flexible procedures */}
          <div className="mt-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-800">
                  SOP (Standard Operating Procedure)
                </label>
                <p className="text-xs text-gray-500">
                  Add procedure cards with a heading and bullet points. Use &quot;Add Procedure&quot; to add as many steps as you need.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {sopProcedures.map((proc, index) => (
                <Card key={proc.id} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex flex-1 min-w-0 flex-col gap-1">
                      <label className="text-xs font-semibold text-gray-500">
                        Procedure heading
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 w-6 text-sm font-semibold text-gray-600">
                          {index + 1}.
                        </span>
                        <Input
                          value={proc.heading}
                          onChange={e => updateSopProcedure(proc.id, 'heading', e.target.value)}
                          placeholder={`e.g. ${index === 0 ? 'Preparation' : index === 1 ? 'Assembly' : 'Final Step'}`}
                          className="flex-1 min-w-0"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSopProcedure(proc.id)}
                      className="mt-6 flex shrink-0 items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                      title="Remove procedure"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Points — enter one per line (shown as bullets below)
                    </label>
                    <textarea
                      value={proc.points}
                      onChange={e => updateSopProcedure(proc.id, 'points', e.target.value)}
                      placeholder="e.g. Step one&#10;Step two&#10;Step three"
                      rows={3}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
                    />
                    {sopLines(proc.points).length > 0 && (
                      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700">
                        {sopLines(proc.points).map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Card>
              ))}
              {sopProcedures.length === 0 && (
                <p className="rounded-xl border border-dashed border-gray-300 bg-gray-50/50 px-4 py-6 text-center text-sm text-gray-500">
                  No procedures yet. Click &quot;Add Procedure&quot; to add a step (heading + points).
                </p>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <IconTextButton
                icon={<PlusCircle className="w-5 h-5" />}
                text="Add Procedure"
                onClick={addSopProcedure}
                type="button"
                variant="default"
                size="sm"
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs font-semibold text-gray-500">
                Description
              </label>
              <div className="flex items-center gap-2">
                {isRecording && (
                  <div className="flex items-center gap-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 border border-red-200">
                    <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
                    <span className="text-xs font-mono font-semibold text-red-700">
                      {formatRecordingTime(recordingTime)}
                    </span>
                    <span className="text-xs text-red-600">/ 10:00</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleToggleRecording}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    isRecording
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title={isRecording ? 'Stop recording' : 'Start voice recording (max 10 minutes)'}
                >
                  <Mic className={`w-4 h-4 ${isRecording ? 'animate-pulse' : ''}`} />
                  <span>{isRecording ? 'Stop Recording' : 'Record'}</span>
                </button>
              </div>
            </div>
            
            {/* Audio Player */}
            {audioUrl && (
              <div className="mb-3 rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <audio
                      controls
                      src={audioUrl}
                      className="w-full h-10"
                      style={{ outline: 'none' }}
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                  <button
                    type="button"
                    onClick={handleDeleteAudio}
                    className="flex-shrink-0 rounded-lg p-2 text-red-600 transition hover:bg-red-50"
                    title="Delete audio"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
            
            <textarea
              value={description}
              onChange={event => setDescription(event.target.value)}
              placeholder="Add product description, notes, or internal instructions..."
              rows={4}
              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
            />
          </div>

          <div className="mt-6">
            <label className="mb-1 block text-xs font-semibold text-gray-500">
              Per Serve (optional)
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={perServe}
              onChange={event => setPerServe(event.target.value)}
            />
            {caloriesPerServe !== null && (
              <div className="mt-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
                <p className="text-sm font-semibold text-gray-700">
                  Calories Per Serve
                </p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {caloriesPerServe.toFixed(2)}
                  <span className="text-sm font-normal text-gray-600 ml-1">kcal</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Per {perServe} serve{parseFloat(perServe) !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>

          {/* Allergen Items Section */}
          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-800">
                  Allergen Items
                </label>
                <p className="text-xs text-gray-500">
                  Allergens detected from the selected raw materials
                  {allergenDetectionMethod && (
                    <span className="ml-2 text-xs text-blue-600 font-medium">
                      ({allergenDetectionMethod === 'llm' ? 'AI Detection' : 'Automatic Detection'})
                    </span>
                  )}
                </p>
              </div>
              {bomEntries.some(entry => entry.raw_material_id && entry.raw_material_id.trim() !== '') && (
                <button
                  type="button"
                  onClick={detectAllergensWithLLM}
                  disabled={detectingAllergensLLM || isSubmitting}
                  className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold transition hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  style={{ color: 'white' }}
                  title="Use AI-powered detection for more accurate allergen detection (takes 2-5 seconds)"
                >
                  {detectingAllergensLLM ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-hidden />
                      <span>Detecting with AI...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <span>Detect with AI</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {detectingAllergensLLM && (
              <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                  <div>
                    <p className="text-sm font-medium text-purple-800">
                      Analyzing ingredients with AI...
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      This may take 2-5 seconds. Please wait...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {allergenItems.length > 0 ? (
              <div className="space-y-4">
                {/* Categorized View (if available) */}
                {allergenData?.categorized && Object.keys(allergenData.categorized).length > 0 && (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Allergens by Category
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(allergenData.categorized).map(([category, items]) => {
                        if (!items || items.length === 0) return null;
                        return (
                          <div key={category} className="border-l-4 border-yellow-400 pl-3">
                            <h5 className="text-xs font-semibold text-gray-600 uppercase mb-1">
                              {category}
                            </h5>
                            <div className="flex flex-wrap gap-2">
                              {items.map((item, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center rounded-lg bg-yellow-100 px-2.5 py-1 text-xs font-medium text-yellow-800 border border-yellow-200"
                                >
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Flat List View */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    All Allergens
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {allergenItems.map((allergen, index) => {
                      // Handle both string and object formats
                      const allergenName = typeof allergen === 'string' ? allergen : allergen.name;
                      const allergenCategory = typeof allergen === 'object' ? allergen.category : null;
                      
                      return (
                        <span
                          key={index}
                          className="inline-flex items-center rounded-lg bg-yellow-100 px-3 py-1.5 text-sm font-medium text-yellow-800 border border-yellow-200"
                          title={allergenCategory ? `Category: ${allergenCategory}` : ''}
                        >
                          {allergenName}
                          {allergenCategory && (
                            <span className="ml-2 text-xs text-yellow-600">
                              ({allergenCategory})
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm text-gray-500 italic">
                  {detectingAllergensLLM
                    ? 'Detecting allergens...'
                    : 'No allergens detected.'}
                </p>
              </div>
            )}

         {error && (
            <div className="mt-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          )}
          {success && (
            <div className="mt-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          )}
          </div>

          <div className="mt-8 flex justify-end">
            <PrimaryButton
              type="submit"
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting
                ? isEditMode
                  ? 'Saving...'
                  : 'Creating...'
                : isEditMode
                ? 'Save'
                : 'Create Product Template'}
            </PrimaryButton>
          </div>
        </div>
      </form>

      {/* Image Upload Retry/Skip Dialog */}
      {showUploadRetryDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={e => {
            // Close dialog if clicking on the backdrop
            if (e.target === e.currentTarget) {
              handleSkipImageUpload();
            }
          }}
        >
          <Card.Light className="w-full max-w-md p-6 mx-4 relative">
            <button
              type="button"
              onClick={handleSkipImageUpload}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-semibold text-gray-800 mb-4 pr-8">
              Image Upload Failed
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              The product template was created successfully, but the image upload
              failed.
            </p>
            {uploadError && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-medium text-yellow-800">
                  Error: {uploadError.message || 'Unknown error'}
                </p>
                {uploadError.error === 'CORS Error' && (
                  <p className="text-xs text-yellow-700 mt-1">
                    This is likely a CORS configuration issue. Check the browser
                    console for details.
                  </p>
                )}
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleSkipImageUpload}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isSubmitting}
              >
                Skip Photo
              </button>
              <button
                type="button"
                onClick={handleRetryImageUpload}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Retrying...' : 'Retry Upload'}
              </button>
            </div>
          </Card.Light>
        </div>
      )}
      <SuccessOverlay
        open={showSuccessScreen}
        onClose={() => setShowSuccessScreen(false)}
        title={
          isEditMode
            ? 'Product Template Updated Successfully'
            : 'Product Template Created Successfully'
        }
        message={success || undefined}
        buttonLabel="Done"
      />
    </div>
  );
};

export default ProductTemplate;
